<template>
  <div class="sliding-puzzle flex flex-col items-center w-full">
    <div class="relative w-full max-w-[500px] aspect-square bg-[#1c4848] rounded-2xl shadow-2xl p-2.5">
      <div class="relative w-full h-full rounded-lg overflow-hidden bg-[#1c4848]">
        <template v-for="{ tileId, posIndex } in tileList" :key="tileId">
          <button
            v-if="tileId !== 0"
            type="button"
            class="tile-slide absolute border border-white/20 cursor-pointer hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#ecd79f] focus:ring-inset"
            :style="tilePositionStyle(posIndex)"
            @click="move(posIndex)"
          >
            <div class="w-full h-full bg-no-repeat bg-cover" :style="tileBgStyle(tileId)" />
          </button>
          <div
            v-else
            class="tile-slide absolute flex items-center justify-center bg-[#1c4848]/20 border border-white/10 pointer-events-none"
            :style="tilePositionStyle(posIndex)"
          >
            <span class="text-[10px] uppercase font-bold text-white/20">Empty</span>
          </div>
        </template>
      </div>
    </div>
    <div
      v-show="won"
      class="mt-6 bg-[#1c4848] text-[#ecd79f] px-6 py-3 rounded-2xl font-bold shadow-xl"
    >
      {{ winMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { parseSlidingGridSize } from './parseGameConfig.js';

const props = defineProps({
  game: { type: Object, required: true },
});

const emit = defineEmits(['win', 'stats']);

const config = computed(() => props.game?.config || {});
const n = computed(() => parseSlidingGridSize(config.value.grid_size));

const imageUrl = computed(() => {
  const a = props.game?.assets || [];
  const first = Array.isArray(a) ? a[0] : null;
  return first?.url || '';
});

const winMessage = computed(() => config.value.win_message || 'Mashallah! Great Job!');

const board = ref([]);
const won = ref(false);
const moves = ref(0);
const startTime = ref(null);
const elapsedSeconds = ref(0);
let timerId = null;

function sendStats() {
  emit('stats', { moves: moves.value, time: elapsedSeconds.value });
}

function getNeighbors(idx) {
  const row = Math.floor(idx / n.value);
  const col = idx % n.value;
  const out = [];
  if (col > 0) out.push(idx - 1);
  if (col < n.value - 1) out.push(idx + 1);
  if (row > 0) out.push(idx - n.value);
  if (row < n.value - 1) out.push(idx + n.value);
  return out;
}

function shuffleBoard() {
  const size = n.value * n.value;
  const arr = [];
  for (let i = 1; i < size; i++) arr.push(i);
  arr.push(0);
  let emptyIdx = size - 1;
  for (let move = 0; move < size * 25; move++) {
    const neighbors = getNeighbors(emptyIdx);
    const swapIdx = neighbors[Math.floor(Math.random() * neighbors.length)];
    [arr[emptyIdx], arr[swapIdx]] = [arr[swapIdx], arr[emptyIdx]];
    emptyIdx = swapIdx;
  }
  board.value = arr;
  won.value = false;
  moves.value = 0;
  startTime.value = Date.now();
  elapsedSeconds.value = 0;
  sendStats();
  if (timerId) clearInterval(timerId);
  timerId = setInterval(() => {
    if (won.value) return;
    elapsedSeconds.value = Math.floor((Date.now() - startTime.value) / 1000);
    sendStats();
  }, 1000);
}

function move(idx) {
  const emptyIdx = board.value.indexOf(0);
  const neighbors = getNeighbors(emptyIdx);
  if (!neighbors.includes(idx)) return;
  const next = [...board.value];
  [next[emptyIdx], next[idx]] = [next[idx], next[emptyIdx]];
  board.value = next;
  moves.value += 1;
  sendStats();
  checkWin();
}

function checkWin() {
  const size = n.value * n.value;
  const ok = board.value.every((v, i) => v === (i + 1) % size);
  if (ok) {
    won.value = true;
    if (timerId) clearInterval(timerId);
    emit('win');
  }
}

const cellPct = computed(() => 100 / n.value);

const tileList = computed(() => {
  const size = n.value * n.value;
  const posBy = {};
  board.value.forEach((val, idx) => { posBy[val] = idx; });
  return Array.from({ length: size }, (_, tileId) => ({ tileId, posIndex: posBy[tileId] }));
});

function tilePositionStyle(posIndex) {
  const row = Math.floor(posIndex / n.value);
  const col = posIndex % n.value;
  return {
    width: `${cellPct.value}%`,
    height: `${cellPct.value}%`,
    top: `${row * cellPct.value}%`,
    left: `${col * cellPct.value}%`,
  };
}

function tileBgStyle(tileId) {
  const size = n.value;
  const row = Math.floor((tileId - 1) / size);
  const col = (tileId - 1) % size;
  const denom = size > 1 ? size - 1 : 1;
  const pctX = (100 * col) / denom;
  const pctY = (100 * row) / denom;
  return {
    backgroundImage: `url(${imageUrl.value})`,
    backgroundSize: `${size * 100}% ${size * 100}%`,
    backgroundPosition: `${pctX}% ${pctY}%`,
  };
}

watch(() => [props.game?.id, n.value, imageUrl.value], () => {
  if (imageUrl.value) shuffleBoard();
}, { immediate: true });

onBeforeUnmount(() => {
  if (timerId) clearInterval(timerId);
});

defineExpose({ restart: shuffleBoard });
</script>

<style scoped>
.tile-slide {
  transition: top 0.28s cubic-bezier(0.34, 1.2, 0.64, 1), left 0.28s cubic-bezier(0.34, 1.2, 0.64, 1);
}
</style>
